/**
 * Mercado entre jugadores.
 *
 * Dos pestañas porque son dos tareas distintas: mirar qué hay y poner algo a la
 * venta. Mezclarlas obligaría a distinguir constantemente entre "esta carta la
 * vende alguien" y "esta carta la vendo yo".
 *
 * En comprar se muestra siempre el precio ANTES del botón y el saldo restante
 * después: en un juego con economía, la sorpresa al pulsar es lo que quema la
 * confianza. Y en vender se enseña lo que se cobra de verdad, ya descontada la
 * comisión, porque el número que importa es ese y no el que se escribe.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Coins, Loader2, Store, Tag, TriangleAlert, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CardView } from '@/components/cards/CardView';
import { Button } from '@/components/ui/Button';
import { getCardMaster } from '@/data';
import {
  useBuyListing,
  useCancelListing,
  useCreateListing,
  useMarketOffers,
  useMyListings,
  type MarketOffer,
} from '@/hooks/useMarket';
import { MARKET_FEE_RATE } from '@/types/game';
import type { CollectionCard, Deck, Profile } from '@/types/game';

interface MarketScreenProps {
  profile: Profile | null;
  inventory: readonly CollectionCard[];
  deck: Deck | null;
  userId: string | undefined;
  isOffline: boolean;
}

type Tab = 'buy' | 'sell';

const formatter = new Intl.NumberFormat('es-ES');

function OfferCard({
  offer,
  gold,
  isMine,
  onBuy,
  isBusy,
}: {
  offer: MarketOffer;
  gold: number;
  isMine: boolean;
  onBuy: () => void;
  isBusy: boolean;
}) {
  const master = getCardMaster(offer.masterId);
  const affordable = gold >= offer.priceGold;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="flex flex-col gap-2 rounded-xl border border-white/10 bg-slate-900/60 p-2"
    >
      <div className="mx-auto w-20">
        <CardView
          card={{
            instanceId: offer.userCardId,
            masterId: offer.masterId,
            level: offer.level,
            owner: 'PLAYER',
          }}
          variant="board"
        />
      </div>

      <div className="text-center">
        <p className="truncate text-xs font-semibold text-white">{master.name}</p>
        <p className="text-[10px] text-slate-400">
          {offer.level > 1 ? `Nivel ${offer.level} · ` : ''}
          {offer.serialNumber !== null && offer.maxSupply !== null
            ? `#${offer.serialNumber} de ${offer.maxSupply}`
            : 'Tirada libre'}
        </p>
        <p className="truncate text-[10px] text-slate-500">de {offer.sellerUsername}</p>
      </div>

      <div className="flex items-center justify-center gap-1 text-sm font-bold text-amber-200">
        <Coins className="h-3.5 w-3.5" aria-hidden />
        {formatter.format(offer.priceGold)}
      </div>

      {isMine ? (
        <p className="text-center text-[10px] text-slate-500">Tu anuncio</p>
      ) : (
        <Button size="sm" disabled={!affordable || isBusy} onClick={onBuy}>
          {affordable ? 'Comprar' : 'Sin oro'}
        </Button>
      )}
    </motion.div>
  );
}

export function MarketScreen({ profile, inventory, deck, userId, isOffline }: MarketScreenProps) {
  const [tab, setTab] = useState<Tab>('buy');
  const [error, setError] = useState<string | null>(null);
  const [selling, setSelling] = useState<string | null>(null);
  const [price, setPrice] = useState('100');

  const offers = useMarketOffers();
  const mine = useMyListings(userId);
  const buy = useBuyListing(userId);
  const list = useCreateListing(userId);
  const cancel = useCancelListing(userId);

  /**
   * Cartas que se pueden poner a la venta.
   *
   * Se excluyen las del mazo activo y las no comerciables por la misma razón por
   * la que lo hace el servidor: vender por accidente una carta del mazo es un
   * error irreversible. Aquí solo se adelanta el filtro para que no aparezcan.
   */
  const sellable = useMemo(() => {
    const inDeck = new Set(deck?.cardIds ?? []);
    const listed = new Set(
      (mine.data ?? []).filter((row) => row.status === 'ACTIVE').map((row) => row.user_card_id),
    );
    return inventory.filter(
      (card) => card.isTradable && !inDeck.has(card.id) && !listed.has(card.id),
    );
  }, [inventory, deck, mine.data]);

  const activeListings = (mine.data ?? []).filter((row) => row.status === 'ACTIVE');

  if (isOffline) {
    return (
      <div className="grid flex-1 place-items-center p-6 text-center">
        <p className="max-w-xs text-sm text-slate-400">
          El mercado necesita cuenta. En modo local se juega con el mazo inicial.
        </p>
      </div>
    );
  }

  const run = (action: () => void) => {
    setError(null);
    action();
  };

  const priceNumber = Number.parseInt(price, 10);
  const priceValid = Number.isInteger(priceNumber) && priceNumber >= 1 && priceNumber <= 1_000_000;
  const payout = priceValid ? priceNumber - Math.floor(priceNumber * MARKET_FEE_RATE) : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-2 sm:p-4">
      <div className="mx-auto w-full max-w-4xl">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {(
              [
                ['buy', 'Comprar'],
                ['sell', 'Vender'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`rounded-md px-2.5 py-1 text-sm font-semibold transition ${
                  tab === id ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-amber-200">
            <Coins className="h-3.5 w-3.5" aria-hidden />
            {formatter.format(profile?.gold ?? 0)}
          </span>
        </div>

        <AnimatePresence>
          {error ? (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              role="alert"
              className="mb-3 flex items-center gap-1.5 rounded-lg bg-rose-950/70 px-3 py-2 text-xs text-rose-200 ring-1 ring-rose-500/40"
            >
              <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden />
              {error}
            </motion.p>
          ) : null}
        </AnimatePresence>

        {tab === 'buy' ? (
          offers.isLoading ? (
            <div className="grid place-items-center py-10 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            </div>
          ) : (offers.data ?? []).length === 0 ? (
            <div className="grid place-items-center gap-2 py-12 text-center">
              <Store className="h-8 w-8 text-slate-600" aria-hidden />
              <p className="text-sm text-slate-500">
                No hay nada a la venta. Sé el primero en publicar algo.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
              <AnimatePresence mode="popLayout">
                {(offers.data ?? []).map((offer) => (
                  <OfferCard
                    key={offer.listingId}
                    offer={offer}
                    gold={profile?.gold ?? 0}
                    isMine={offer.sellerId === userId}
                    isBusy={buy.isPending}
                    onBuy={() =>
                      run(() =>
                        buy.mutate(offer.listingId, {
                          onError: (mutationError) => setError(mutationError.message),
                        }),
                      )
                    }
                  />
                ))}
              </AnimatePresence>
            </div>
          )
        ) : (
          <div className="space-y-5">
            {activeListings.length > 0 ? (
              <section>
                <h3 className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">
                  A la venta ahora
                </h3>
                <ul className="space-y-1.5">
                  {activeListings.map((row) => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between gap-2 rounded-lg bg-slate-800/60 px-3 py-2"
                    >
                      <span className="truncate text-sm text-slate-200">
                        {getCardMaster(
                          inventory.find((c) => c.id === row.user_card_id)?.masterId ??
                            'card_squire_01',
                        ).name}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 text-sm font-bold text-amber-200">
                          <Coins className="h-3.5 w-3.5" aria-hidden />
                          {formatter.format(row.price_gold)}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={cancel.isPending}
                          onClick={() =>
                            run(() =>
                              cancel.mutate(row.id, {
                                onError: (mutationError) => setError(mutationError.message),
                              }),
                            )
                          }
                        >
                          <X className="h-3 w-3" aria-hidden />
                          Retirar
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-slate-400 uppercase">
                Tus cartas vendibles ({sellable.length})
              </h3>
              {sellable.length === 0 ? (
                <p className="py-6 text-center text-xs text-slate-500">
                  No tienes cartas sueltas. Las del mazo activo no se pueden vender.
                </p>
              ) : (
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8">
                  {sellable.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => {
                        setSelling(selling === card.id ? null : card.id);
                        setError(null);
                      }}
                      className={`relative rounded-lg transition ${
                        selling === card.id ? 'ring-2 ring-amber-300' : ''
                      }`}
                      aria-label={`Vender ${getCardMaster(card.masterId).name}`}
                    >
                      <CardView
                        card={{
                          instanceId: card.id,
                          masterId: card.masterId,
                          level: card.level,
                          owner: 'PLAYER',
                        }}
                        variant="board"
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>

            {selling ? (
              <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-amber-400/30 bg-slate-900/80 p-3"
              >
                <div className="flex items-end gap-2">
                  <label className="flex-1">
                    <span className="mb-1 block text-xs text-slate-400">Precio en oro</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={1000000}
                      value={price}
                      onChange={(event) => setPrice(event.target.value)}
                      className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white focus:border-amber-400 focus:outline-none"
                    />
                  </label>
                  <Button
                    disabled={!priceValid || list.isPending}
                    onClick={() =>
                      run(() =>
                        list.mutate(
                          { cardId: selling, price: priceNumber },
                          {
                            onSuccess: () => setSelling(null),
                            onError: (mutationError) => setError(mutationError.message),
                          },
                        ),
                      )
                    }
                  >
                    <Tag className="h-4 w-4" aria-hidden />
                    Publicar
                  </Button>
                </div>
                {/* Lo que de verdad cobra el vendedor, no lo que escribe. */}
                <p className="mt-2 text-[11px] text-slate-400">
                  {priceValid
                    ? `Recibirás ${formatter.format(payout)} de oro (comisión del ${MARKET_FEE_RATE * 100} %).`
                    : 'El precio debe estar entre 1 y 1 000 000.'}
                </p>
              </motion.section>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
